import { GetServerSidePropsContext } from 'next';
import { dbDateToTimestamp } from '@utils/date';

/**
 * Function to convert Timestamp-like objects in a given object to actual Timestamp objects.
 * This function is used to reinitialize the Timestamp objects after they have been serialized and deserialized.
 *
 * @template T - The type of the input object. It must extend the object type.
 * @param {T} data - The object in which to convert Timestamp-like objects to Timestamp objects.
 * @returns {T} - The object with converted Timestamp objects.
 *
 * @example
 *
 * const obj = {
 *   date: {
 *     seconds: 1609459200,
 *     nanoseconds: 0
 *   },
 *   nested: {
 *     date: {
 *       _seconds: 1609459200,
 *       _nanoseconds: 0
 *     }
 *   }
 * };
 *
 * const convertedObj = convertTimestamps(obj);
 *
 * console.log(convertedObj.date instanceof Timestamp); // true
 * console.log(convertedObj.nested.date instanceof Timestamp); // true
 */
const convertTimestamps = <T>(data: T): T => {
    if (data === null || data === undefined) {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map((item) => convertTimestamps(item)) as unknown as T;
    }

    if (
        Object.prototype.hasOwnProperty.call(data, '_seconds') ||
        Object.prototype.hasOwnProperty.call(data, 'seconds')
    ) {
        return dbDateToTimestamp(data as never) as unknown as T;
    }

    if (typeof data === 'object') {
        Object.keys(data).forEach((key) => {
            data[key as keyof T] = convertTimestamps(data[key as keyof T]);
        });
    }

    return data;
};

/**
 * Function to set cache headers for the response.
 * The cache headers are set to cache the response for 24 hours and revalidate it after 1 hour.
 *
 * @param ctx - The context object of the getServerSideProps function.
 */
const setCacheHeaders = (ctx: GetServerSidePropsContext) => {
    ctx.res.setHeader('Cache-Control', 'public, s-maxage=86400, stale-while-revalidate=3600');
};

/**
 * Function to check if the Firebase token is expired.
 * The function checks if the token is expired by comparing the expiration time of the token with the current time.
 * If the expiration time is less than the current time, the token is considered expired.
 * The function returns true if the token is expired, otherwise it returns false.
 */
const isTokenExpired = (token: string): boolean => {
    const tokenPayload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
    const expirationTime = tokenPayload.exp * 1000;
    const currentTime = Date.now();
    if (expirationTime < currentTime) {
        return true;
    }

    return false;
};

export { convertTimestamps, isTokenExpired, setCacheHeaders };
